import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2, Loader2 } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAdmin } from "@/contexts/AdminContext";

/**
 * Premium video card — Instagram/TikTok-style single-tap immersive playback.
 *
 * Behaviour:
 *  • On mount with autoplay=true (project page): video arms immediately, plays
 *    MUTED (universally allowed by Android Chrome / Samsung Internet / iOS
 *    Safari without a fresh gesture). A subtle "Tap for sound" pill invites
 *    the user to unmute. ONE tap unmutes — no second play tap required.
 *  • On gallery tiles (autoplay=false): static poster with a Play overlay
 *    (the tile navigates to the project page; video doesn't autoplay in feed).
 *  • Polls /api/files/status while the file is still being transcoded.
 *
 * Props:
 *   src         video file URL
 *   poster      poster URL
 *   fileId      db.files id (for processing-status polling)
 *   processing  initial processing state from server
 *   autoplay    if true, auto-arm + auto-play muted on mount
 *   className   extra outer classes
 *   aspectClass tailwind aspect class
 *   showControls native controls after first interaction
 *   fitClass    object-fit class for the <video>
 */
export default function VideoCard({
  src,
  poster,
  fileId,
  processing = false,
  autoplay = false,
  className = "",
  aspectClass = "aspect-square",
  showControls = true,
  fitClass = "object-cover",
}) {
  const ref = useRef(null);
  const [armed, setArmed] = useState(autoplay);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true); // start muted so autoplay is allowed
  const [hasUnmuted, setHasUnmuted] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [livePoster, setLivePoster] = useState(poster);
  const { isAdmin } = useAdmin();
  // NEVER show the "Processing…" overlay to non-admin visitors. The original
  // upload bytes are already at the URL and play in every modern browser, so
  // visitors get a normal video experience even if the backend pipeline is
  // mid-transcode or has silently crashed. Only the admin sees the badge as a
  // hint that the auto-poster is still being generated.
  const [isProcessing, setIsProcessing] = useState(isAdmin ? processing : false);
  const [forceCompleting, setForceCompleting] = useState(false);

  useEffect(() => {
    setIsProcessing(isAdmin ? processing : false);
    setLivePoster(poster);
  }, [processing, poster, isAdmin]);

  // Poll backend until the video has been transcoded. After ~45s of
  // continuous "still processing" responses we give up and unblock
  // playback anyway — the original upload bytes are already at the URL
  // and most browsers will play them. This keeps visitors from seeing
  // a perpetually-spinning tile when ffmpeg crashed silently in the
  // background and the watchdog hasn't yet fired.
  useEffect(() => {
    if (!isProcessing || !fileId) return undefined;
    let cancelled = false;
    let polls = 0;
    const tick = async () => {
      polls += 1;
      try {
        const { data } = await api.get(`/files/status/${fileId}`);
        if (cancelled) return;
        if (!data.processing) {
          setIsProcessing(false);
          if (data.poster_url) setLivePoster(data.poster_url);
        } else if (polls >= 12) {
          // 12 polls × 4s = 48s of waiting — give up and play the original.
          setIsProcessing(false);
        }
      } catch {
        if (polls >= 12 && !cancelled) setIsProcessing(false);
      }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isProcessing, fileId]);

  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    const onWait = () => setBuffering(true);
    const onPlay = () => { setBuffering(false); setPlaying(true); };
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);
    const onCanPlay = () => setBuffering(false);
    v.addEventListener("waiting", onWait);
    v.addEventListener("playing", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("ended", onEnded);
    v.addEventListener("canplay", onCanPlay);
    return () => {
      v.removeEventListener("waiting", onWait);
      v.removeEventListener("playing", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("ended", onEnded);
      v.removeEventListener("canplay", onCanPlay);
    };
  }, [armed]);

  // Auto-play once metadata is available (single-tap-from-tile UX)
  useEffect(() => {
    if (!autoplay || isProcessing) return;
    const v = ref.current;
    if (!v) return;
    setArmed(true);
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === "function") {
        p.then(() => setAutoplayBlocked(false)).catch(() => setAutoplayBlocked(true));
      }
    };
    if (v.readyState >= 1) tryPlay();
    else v.addEventListener("loadedmetadata", tryPlay, { once: true });
    return () => v.removeEventListener("loadedmetadata", tryPlay);
  }, [autoplay, isProcessing, src]);

  const handlePlay = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (isProcessing) return;
    if (!armed) setArmed(true);
    const v = ref.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  const unmute = (e) => {
    e?.stopPropagation?.();
    setMuted(false);
    setHasUnmuted(true);
    const v = ref.current;
    if (v && v.paused) v.play().catch(() => {});
  };

  const toggleMute = (e) => {
    e?.stopPropagation?.();
    setMuted((m) => !m);
    setHasUnmuted(true);
  };

  const toggleFullscreen = (e) => {
    e?.stopPropagation?.();
    const v = ref.current;
    if (!v) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else (v.requestFullscreen || v.webkitEnterFullscreen)?.call(v);
  };

  const resolvedSrc = src ? fileUrl(src) : "";
  const resolvedPoster = livePoster ? fileUrl(livePoster) : undefined;
  const showSoundHint = autoplay && armed && !hasUnmuted && muted && !isProcessing;

  return (
    <div
      className={`relative ${aspectClass} bg-black overflow-hidden select-none ${className}`}
      data-testid="video-card"
    >
      <video
        ref={ref}
        src={armed && !isProcessing ? resolvedSrc : undefined}
        poster={resolvedPoster}
        preload={autoplay ? "auto" : (armed ? "auto" : "none")}
        autoPlay={autoplay && !isProcessing}
        playsInline
        muted={muted}
        loop={autoplay}
        controls={armed && showControls && !isProcessing && hasUnmuted}
        controlsList="nodownload noremoteplayback noplaybackrate nofullscreen"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        className={`w-full h-full ${fitClass}`}
        onClick={(e) => {
          e.stopPropagation();
          // Tap on the video itself: if hint is showing, treat tap as unmute;
          // otherwise toggle play/pause for natural mobile feel.
          if (showSoundHint) unmute(e);
          else handlePlay(e);
        }}
      />

      {!armed && resolvedPoster && (
        <img
          src={resolvedPoster}
          alt=""
          loading="lazy"
          className={`absolute inset-0 w-full h-full ${fitClass} pointer-events-none`}
        />
      )}

      {/* Processing overlay */}
      {isProcessing && (
        <div className="absolute inset-0 bg-black/65 backdrop-blur-sm flex flex-col items-center justify-center text-white" data-testid="video-card-processing">
          <Loader2 className="w-7 h-7 animate-spin mb-2 pointer-events-none" />
          <div className="text-[10px] tracking-[0.3em] uppercase opacity-80 pointer-events-none">Processing video…</div>
          <div className="text-[10px] opacity-60 mt-0.5 pointer-events-none">This may take a moment</div>
          {isAdmin && fileId && (
            <button
              type="button"
              data-testid="video-card-force-complete"
              disabled={forceCompleting}
              onClick={async (e) => {
                e.stopPropagation();
                if (forceCompleting) return;
                setForceCompleting(true);
                try {
                  await api.post(`/admin/files/${fileId}/force-complete`);
                  setIsProcessing(false);
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error("Force complete failed", err);
                  setForceCompleting(false);
                }
              }}
              className="mt-3 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 ring-1 ring-white/20 text-[10px] tracking-[0.2em] uppercase disabled:opacity-50"
            >
              {forceCompleting ? "Clearing…" : "Mark ready (admin)"}
            </button>
          )}
        </div>
      )}

      {/* "Tap for sound" hint — premium Instagram/TikTok pattern */}
      {showSoundHint && (
        <button
          type="button"
          onClick={unmute}
          aria-label="Tap for sound"
          data-testid="video-card-unmute-hint"
          className="absolute top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/65 backdrop-blur-md text-white text-xs ring-1 ring-white/15 animate-pulse"
        >
          <VolumeX className="w-3.5 h-3.5" /> Tap for sound
        </button>
      )}

      {/* Center Play overlay — gallery feel OR autoplay-blocked rescue */}
      {((!autoplay && !isProcessing && (!armed || !playing)) ||
        (autoplay && autoplayBlocked && !playing && !isProcessing)) && (
        <button
          type="button"
          onClick={handlePlay}
          aria-label="Play video"
          data-testid="video-card-play"
          className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/20 active:bg-black/30 transition-colors"
        >
          <span className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-black/55 backdrop-blur-md flex items-center justify-center ring-1 ring-white/15">
            {buffering ? (
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            ) : (
              <Play className="w-6 h-6 text-white fill-white ml-0.5" />
            )}
          </span>
        </button>
      )}

      {/* Initial-load spinner during autoplay before first frame */}
      {autoplay && armed && !playing && buffering && !isProcessing && !autoplayBlocked && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Loader2 className="w-9 h-9 text-white animate-spin drop-shadow-md" />
        </div>
      )}

      {/* Always-visible action chips (mute/fullscreen/pause) once armed */}
      {armed && !isProcessing && (
        <div className="absolute bottom-3 right-3 flex gap-2 pointer-events-auto">
          <button
            type="button"
            onClick={handlePlay}
            aria-label={playing ? "Pause" : "Play"}
            className="w-9 h-9 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-white"
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={toggleMute}
            aria-label={muted ? "Unmute" : "Mute"}
            data-testid="video-card-mute-toggle"
            className="w-9 h-9 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-white"
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label="Fullscreen"
            className="w-9 h-9 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-white"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      )}

      {armed && playing && buffering && !isProcessing && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Loader2 className="w-8 h-8 text-white animate-spin drop-shadow-md" />
        </div>
      )}
    </div>
  );
}
